// Office "LOLER > To close" tab (Session BAQ; the standalone Insurance page until Session BDF). The items an examiner raises in a LOLER report, worked to
// closure: raised, quoted, booked, done, then verified by the office. Nothing closes by itself, the
// office closes each item once it has been dealt with. Server-paginated and server-sorted, so the sort
// and the search go to the server rather than filtering one page of twenty-five.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type {
  LolerItemCategory,
  LolerItemCounts,
  LolerItemSort,
  LolerItemState,
  LolerItemsBackfillPreview,
  LolerItemsBackfillResult,
  LolerTrackedItem,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useListSearch } from "../../lib/useListSearch";
import { formatDate } from "../../lib/format";
import type { SortState } from "../../lib/useSortableColumn";
import { Button } from "../../components/Button";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { MODAL_ACTIONS, MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";
import { OverflowMenu, overflowDangerItemCls, overflowItemCls } from "../../components/OverflowMenu";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { SiteFilterChip, useSiteFilter } from "./customerFilter";

const PAGE_SIZE = 25;

const STATE_OPTIONS: Array<{ value: LolerItemState; label: string }> = [
  { value: "raised", label: "Raised" },
  { value: "quoted", label: "Quoted" },
  { value: "booked", label: "Booked" },
  { value: "done", label: "Done" },
  { value: "verified", label: "Verified" },
];
const STATE_VALUES = STATE_OPTIONS.map((o) => o.value);

const SORT_OPTIONS: Array<{ key: LolerItemSort; label: string }> = [
  { key: "urgency", label: "Urgency" },
  { key: "site", label: "Lift" },
  { key: "state", label: "State" },
  { key: "age", label: "Age" },
  { key: "deadline", label: "Remedy by" },
];
const isSortKey = (k: string | null): k is LolerItemSort => SORT_OPTIONS.some((o) => o.key === k);

function chipClass(active: boolean, tone: "green" | "danger" = "green"): string {
  const on = tone === "danger" ? "border-ele-danger bg-ele-inset text-ele-danger" : "border-ele-green bg-ele-inset text-ele-green";
  return `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
    active ? on : "border-ele-purple-accent text-ele-body hover:text-ele-green"
  }`;
}

const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;

type ActionKind = "done" | "verify" | "reopen";

const ACTION_COPY: Record<ActionKind, { title: string; message: string; confirm: string }> = {
  done: {
    title: "Mark this item done?",
    message: "Record that the work for this item has been carried out. It stays on the list until you verify it.",
    confirm: "Mark done",
  },
  verify: {
    title: "Verify and close this item?",
    message: "Verify and close this item? Its defect will be marked resolved.",
    confirm: "Verify and close",
  },
  reopen: {
    title: "Reopen this item?",
    message: "The item goes back onto the open list and its defect is no longer marked resolved.",
    confirm: "Reopen",
  },
};

// The branded confirm for a change of state: the row button opens it, and nothing is written until the
// confirm button inside it is pressed. An optional note is kept with the change.
function ItemActionModal({
  kind,
  item,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  kind: ActionKind;
  item: LolerTrackedItem;
  busy: boolean;
  error: string | null;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  const copy = ACTION_COPY[kind];
  return (
    <ModalShell ariaLabel={copy.title} onClose={() => !busy && onCancel()} closeOnBackdrop width="max-w-md" panelClassName="p-5 shadow-xl">
      <h2 className="font-heading text-lg text-ele-strong">{copy.title}</h2>
      <p className="mt-2 text-sm text-ele-body">{copy.message}</p>
      <p className="mt-2 rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm text-ele-strong">{item.description}</p>
      <label className="mt-3 block text-xs text-ele-body" htmlFor="insurer-item-note">
        Note (optional)
      </label>
      <textarea
        id="insurer-item-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={2000}
        className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus"
      />
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button className={MODAL_ACTION_BTN} loading={busy} onClick={() => onConfirm(note.trim())}>
          {copy.confirm}
        </Button>
      </div>
    </ModalShell>
  );
}

// Bring the items from reports already on file into the tracker. Previews first, in plain English, and
// only a second, deliberate press writes anything. Nobody outside the business is told.
function BackfillModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [preview, setPreview] = useState<LolerItemsBackfillPreview | null>(null);
  const [result, setResult] = useState<LolerItemsBackfillResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.lolerItems
      .backfillPreview()
      .then((p) => active && setPreview(p))
      .catch((e) => active && setError(e instanceof ApiError ? e.message : "Could not check the reports on file."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  async function commit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.lolerItems.backfillCommit();
      setResult(r);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not bring the items in. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Bring in existing reports" onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="p-5 shadow-xl">
      <h2 className="font-heading text-lg text-ele-strong">Bring in existing reports</h2>
      {loading && <Loader shape="inline" />}
      {!loading && result && (
        <div role="status" className="mt-3 text-sm text-ele-body">
          <p className="text-ele-strong">
            Brought in {result.created} {result.created === 1 ? "item" : "items"} from {result.reports} {result.reports === 1 ? "report" : "reports"}.
          </p>
          {(result.alreadyTracked > 0 || result.duplicates > 0) && (
            <p className="mt-1">
              {result.alreadyTracked} already tracked, {result.duplicates} skipped as duplicates.
            </p>
          )}
          {result.note && <p className="mt-1">{result.note}</p>}
        </div>
      )}
      {!loading && !result && preview && (
        <div className="mt-3 text-sm text-ele-body">
          {preview.items === 0 ? (
            <p>Every item in the reports on file is already being tracked. There is nothing to bring in.</p>
          ) : (
            <>
              <p>
                This will bring in <span className="text-ele-strong">{preview.items}</span> {preview.items === 1 ? "item" : "items"} on{" "}
                <span className="text-ele-strong">{preview.lifts}</span> {preview.lifts === 1 ? "lift" : "lifts"}.{" "}
                {preview.liftsNeedingJob > 0
                  ? `${preview.liftsNeedingJob} ${preview.liftsNeedingJob === 1 ? "lift needs" : "lifts need"} a new follow-up job to check ${preview.liftsNeedingJob === 1 ? "its items" : "their items"}.`
                  : "No new follow-up jobs are needed."}
              </p>
              <p className="mt-2">Nothing is sent to any customer. The items just appear on this page.</p>
              {preview.note && <p className="mt-2">{preview.note}</p>}
              {preview.reports.length > 0 && (
                <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto rounded-ele border border-ele-hairline bg-ele-surface p-2 text-xs">
                  {preview.reports.map((r) => (
                    <li key={r.reportId}>
                      <span className="text-ele-strong">{r.reportRef ?? "Report"}</span>
                      {r.ele ? ` | ${r.ele}` : ""}
                      {r.siteName ? ` | ${r.siteName}` : ""}
                      {r.examinationDate ? ` | ${formatDate(r.examinationDate)}` : ""}
                      {` | ${r.untracked} to bring in`}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onClose} disabled={busy}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && preview && preview.items > 0 && (
          <Button className={MODAL_ACTION_BTN} loading={busy} onClick={() => void commit()}>
            Bring in {preview.items} {preview.items === 1 ? "item" : "items"}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}

export function InsurerItemsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const { siteId, clear: clearSite } = useSiteFilter();
  const { search, setSearch } = useListSearch();

  const assetId = searchParams.get("assetId");
  const reportId = searchParams.get("reportId");
  const q = searchParams.get("q") ?? "";
  const stateParam = searchParams.get("state") ?? "";
  const states = useMemo(
    () => stateParam.split(",").filter((s): s is LolerItemState => (STATE_VALUES as string[]).includes(s)),
    [stateParam],
  );
  const categoryParam = searchParams.get("category");
  const category: LolerItemCategory | "" =
    categoryParam === "immediate" || categoryParam === "timed" || categoryParam === "advisory" ? categoryParam : "";
  const overdueOnly = searchParams.get("overdue") === "true";

  const [sort, setSort] = useState<SortState>({ sortKey: null, sortDir: "asc" });
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<LolerTrackedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<LolerItemCounts | null>(null);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [action, setAction] = useState<{ kind: ActionKind; item: LolerTrackedItem } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LolerTrackedItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showBackfill, setShowBackfill] = useState(false);

  // A response that arrives after a newer request was sent is dropped.
  const requestId = useRef(0);

  const setParam = useCallback(
    (name: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(name, value);
          else next.delete(name);
          return next;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(false);
    try {
      const res = await api.lolerItems.list({
        ...(assetId ? { assetId } : {}),
        ...(siteId ? { siteId } : {}),
        ...(reportId ? { reportId } : {}),
        ...(states.length > 0 ? { state: states } : {}),
        ...(category ? { category } : {}),
        ...(overdueOnly ? { overdue: true } : {}),
        ...(q.trim() ? { q } : {}),
        ...(isSortKey(sort.sortKey) ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
        page,
        pageSize: PAGE_SIZE,
      });
      if (id !== requestId.current) return;
      setRows(res.rows);
      setTotal(res.total);
      setCounts(res.counts);
      setCapped(res.capped === true);
    } catch {
      if (id === requestId.current) setError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [assetId, siteId, reportId, states, category, overdueOnly, q, sort.sortKey, sort.sortDir, page]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleSort(key: string) {
    setPage(1);
    setSort((cur) => {
      if (cur.sortKey !== key) return { sortKey: key, sortDir: "asc" };
      if (cur.sortDir === "asc") return { sortKey: key, sortDir: "desc" };
      return { sortKey: null, sortDir: "asc" };
    });
  }

  function toggleState(s: LolerItemState) {
    const next = states.includes(s) ? states.filter((x) => x !== s) : [...states, s];
    setParam("state", next.length > 0 ? next.join(",") : null);
  }

  function clearFilters() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of ["q", "state", "category", "overdue"]) next.delete(k);
        return next;
      },
      { replace: true },
    );
    setSearch("");
    setPage(1);
  }

  async function runAction(note: string) {
    if (!action) return;
    setActionBusy(true);
    setModalError(null);
    try {
      const call = action.kind === "done" ? api.lolerItems.markDone : action.kind === "verify" ? api.lolerItems.verify : api.lolerItems.reopen;
      const res = await call(action.item.id, note || undefined);
      setNotice(
        action.kind === "verify"
          ? res.defectResolved
            ? "Item verified and closed. Its defect is now resolved."
            : "Item verified and closed."
          : action.kind === "done"
            ? "Item marked done. Verify it once you have checked the work."
            : "Item reopened.",
      );
      setAction(null);
      await load();
    } catch (e) {
      setModalError(e instanceof ApiError ? e.message || e.code : "Could not update the item. Please try again.");
    } finally {
      setActionBusy(false);
    }
  }

  async function runDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.lolerItems.remove(deleteTarget.id);
      setDeleteTarget(null);
      setNotice("Item deleted.");
      await load();
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message || e.code : "Could not delete the item.");
    } finally {
      setDeleteBusy(false);
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersActive = states.length > 0 || category !== "" || overdueOnly || q.trim() !== "";

  const countChips: Array<{ key: string; label: string; count: number | null; active: boolean; danger?: boolean; onClick: () => void }> = [
    { key: "raised", label: "Raised", count: counts?.raised ?? null, active: states.includes("raised"), onClick: () => toggleState("raised") },
    { key: "booked", label: "Booked", count: counts?.booked ?? null, active: states.includes("booked"), onClick: () => toggleState("booked") },
    { key: "done", label: "Done", count: counts?.done ?? null, active: states.includes("done"), onClick: () => toggleState("done") },
    {
      key: "immediate",
      label: "Immediate",
      count: counts?.immediate ?? null,
      active: category === "immediate",
      danger: true,
      onClick: () => setParam("category", category === "immediate" ? null : "immediate"),
    },
    {
      key: "overdue",
      label: "Past remedy-by date",
      count: counts?.overdue ?? null,
      active: overdueOnly,
      danger: true,
      onClick: () => setParam("overdue", overdueOnly ? null : "true"),
    },
  ];

  return (
    <div>
      <PageHeader
        className="mb-4"
        eyebrow="Compliance"
        title="LOLER"
        subtitle="Items from examiners' LOLER reports, worked to closure. They are the examiner's findings, never ELE faults. Close each one yourself once it is dealt with."
        actions={
          canEdit ? (
            <Button variant="secondary" size="sm" onClick={() => setShowBackfill(true)}>
              Bring in existing reports
            </Button>
          ) : undefined
        }
      />

      <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Summary">
        {countChips.map((c) => (
          <button
            key={c.key}
            type="button"
            aria-pressed={c.active}
            aria-label={`${c.label}${c.count != null ? `: ${c.count}` : ""}`}
            onClick={c.onClick}
            className={chipClass(c.active, c.danger ? "danger" : "green")}
          >
            {c.label}
            {c.count != null && <span className="ml-2 font-heading text-sm">{c.count}</span>}
          </button>
        ))}
      </div>

      <ListToolbar
        className="mb-3"
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Item, lift, site or report"
        searchLabel="Search LOLER items"
        resultCount={{ shown: rows.length, total, noun: "items" }}
        sort={{
          state: sort,
          options: SORT_OPTIONS,
          onChange: (s) => {
            setSort(s);
            setPage(1);
          },
        }}
        onClear={filtersActive ? clearFilters : undefined}
        filters={
          <>
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="State">
              {STATE_OPTIONS.map((o) => (
                <button key={o.value} type="button" aria-pressed={states.includes(o.value)} onClick={() => toggleState(o.value)} className={chipClass(states.includes(o.value))}>
                  {o.label}
                </button>
              ))}
            </div>
            <select
              aria-label="Category"
              value={category}
              onChange={(e) => setParam("category", e.target.value || null)}
              className="rounded-ele border border-ele-field-border bg-ele-field px-3 py-1.5 text-sm text-ele-strong"
            >
              <option value="">All categories</option>
              <option value="immediate">Immediate</option>
              <option value="timed">Timed</option>
              <option value="advisory">Advisory</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-ele-body">
              <input type="checkbox" checked={overdueOnly} onChange={(e) => setParam("overdue", e.target.checked ? "true" : null)} />
              Past remedy-by date only
            </label>
          </>
        }
      />

      {(assetId || siteId || reportId) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {assetId && (
            <span className="flex items-center gap-2 rounded-ele border border-ele-blue bg-ele-surface px-3 py-1.5 text-xs text-ele-blue">
              Showing only this lift's items
              <button type="button" onClick={() => setParam("assetId", null)} className="text-ele-green hover:underline">
                Clear filter
              </button>
            </span>
          )}
          {reportId && (
            <span className="flex items-center gap-2 rounded-ele border border-ele-blue bg-ele-surface px-3 py-1.5 text-xs text-ele-blue">
              Showing only this report's items
              <button type="button" onClick={() => setParam("reportId", null)} className="text-ele-green hover:underline">
                Clear filter
              </button>
            </span>
          )}
          {siteId && <SiteFilterChip noun="items" onClear={clearSite} />}
        </div>
      )}

      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}

      {loading && rows.length === 0 ? (
        <Loader />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the LOLER items. Please try again in a moment.
        </p>
      ) : rows.length === 0 ? (
        filtersActive || assetId || siteId || reportId ? (
          <EmptyState role="status" message="No items match these filters." />
        ) : (
          <EmptyState
            message="No open LOLER items."
            hint="Items appear here when an examiner's report lists work to be done."
          />
        )
      ) : (
        <>
          <div data-hscroll className="max-h-[75vh] max-w-full overflow-auto rounded-ele border border-ele-purple-accent" data-testid="insurance-scroll">
            <table className="ele-list-table w-full text-left text-sm" style={{ minWidth: 1180 }}>
              <colgroup>
                <col style={{ width: 150 }} />
                <col style={{ width: 300 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 170 }} />
                <col style={{ width: 190 }} />
              </colgroup>
              <thead className="sticky top-0 z-20 font-menu [&>tr>th]:bg-ele-card text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <SortableHeader label="Lift / site" colKey="site" sort={sort} onToggle={toggleSort} className="md:sticky md:left-0 md:z-30" />
                  <SortableHeader label="Item" colKey="urgency" sort={sort} onToggle={toggleSort} />
                  <SortableHeader label="State" colKey="state" sort={sort} onToggle={toggleSort} />
                  <SortableHeader label="Age" colKey="age" sort={sort} onToggle={toggleSort} />
                  <SortableHeader label="Remedy by" colKey="deadline" sort={sort} onToggle={toggleSort} />
                  <th className="px-3 py-2">Report</th>
                  <th className="px-3 py-2">Engineer's answer</th>
                  <th className="px-3 py-2">Tracked job</th>
                  <th className="cell-actions md:sticky md:right-0 md:z-30 px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <tr key={it.id} className="border-t border-ele-purple-accent align-top">
                    <td className="bg-ele-card px-3 py-2 md:sticky md:left-0 md:z-10">
                      {it.assetId ? (
                        <Link to={`/office/lifts/${encodeURIComponent(it.assetId)}`} className="font-heading text-ele-strong hover:text-ele-green hover:underline">
                          {it.ele ?? "Lift"}
                        </Link>
                      ) : (
                        <span className="font-heading text-ele-strong">{it.ele ?? "No lift"}</span>
                      )}
                      <div className="truncate text-xs text-ele-body" title={it.siteName ?? undefined}>
                        {it.siteName ?? ""}
                      </div>
                    </td>
                    <td className="cell-wrap px-3 py-2 whitespace-normal">
                      <div className="break-words" data-testid="insurance-item-text">
                        {it.description}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 whitespace-normal">
                        <StatusPill domain="lolerCategory" status={it.category} />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill domain="lolerItemState" status={it.state} />
                    </td>
                    <td className="px-3 py-2">{days(it.ageDays)}</td>
                    <td className="px-3 py-2">
                      {it.remedyBy ? (
                        <span className={it.pastRemedyBy ? toneTextClass("red") : undefined}>
                          {formatDate(it.remedyBy)}
                          {it.pastRemedyBy ? " (past)" : ""}
                        </span>
                      ) : (
                        <span className="text-ele-body opacity-70">No date</span>
                      )}
                    </td>
                    <td className="px-3 py-2" title={it.reportRef ?? undefined}>
                      {it.reportId ? (
                        <Link to={`/office/loler/${encodeURIComponent(it.reportId)}`} className="text-ele-green underline-offset-2 hover:underline">
                          {it.reportRef ?? "Open report"}
                        </Link>
                      ) : (
                        (it.reportRef ?? "")
                      )}
                    </td>
                    <td className="cell-wrap px-3 py-2 whitespace-normal">
                      {it.checkOutcomeLabel ? (
                        <>
                          <span className={it.checkOutcome === "needs_work" ? toneTextClass("amber") : undefined}>{it.checkOutcomeLabel}</span>
                          {it.checkedAt && <span className="block text-xs text-ele-body">{formatDate(it.checkedAt)}</span>}
                          {it.checkNote && (
                            <span className="block truncate text-xs text-ele-body" title={it.checkNote}>
                              {it.checkNote}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-ele-body opacity-70">Not checked yet</span>
                      )}
                    </td>
                    <td className="cell-wrap px-3 py-2 whitespace-normal">
                      {it.followUpJob ? (
                        <Link
                          to={`/office/scheduling?job=${encodeURIComponent(it.followUpJob.id)}`}
                          className="text-ele-green underline-offset-2 hover:underline"
                        >
                          {it.followUpJob.engineerName ?? "No engineer yet"}
                          {it.followUpJob.scheduledFor ? `, ${formatDate(it.followUpJob.scheduledFor)}` : ", not dated"}
                        </Link>
                      ) : (
                        <>
                          <span>Not booked yet</span>
                          <span className="block text-xs text-ele-body">
                            Book it from <Link to="/office/scheduling" className="text-ele-green hover:underline">Scheduling</Link> under Unscheduled work.
                          </span>
                        </>
                      )}
                    </td>
                    <td className="cell-actions bg-ele-card md:sticky md:right-0 md:z-10 px-3 py-2 text-right">
                      {canEdit && (
                        <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                          {it.state !== "verified" && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setModalError(null);
                                setAction({ kind: it.state === "done" ? "verify" : "done", item: it });
                              }}
                            >
                              {it.state === "done" ? "Verify" : "Mark done"}
                            </Button>
                          )}
                          <OverflowMenu label={`More actions for ${it.description}`}>
                            {it.state === "raised" || it.state === "quoted" || it.state === "booked" ? (
                              <button
                                type="button"
                                className={overflowItemCls}
                                onClick={() => {
                                  setModalError(null);
                                  setAction({ kind: "verify", item: it });
                                }}
                              >
                                Verify and close
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={overflowItemCls}
                                onClick={() => {
                                  setModalError(null);
                                  setAction({ kind: "reopen", item: it });
                                }}
                              >
                                Reopen
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                type="button"
                                className={overflowDangerItemCls}
                                onClick={() => {
                                  setDeleteError(null);
                                  setDeleteTarget(it);
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </OverflowMenu>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-ele-body">
            <span>
              {total} {total === 1 ? "item" : "items"}
              {capped ? " (the list is capped, narrow the filters to see the rest)" : ""}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <span>
                  Page {page} of {pageCount}
                </span>
                <Button variant="ghost" size="sm" disabled={page >= pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                  Next
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {action && (
        <ItemActionModal
          kind={action.kind}
          item={action.item}
          busy={actionBusy}
          error={modalError}
          onConfirm={(note) => void runAction(note)}
          onCancel={() => setAction(null)}
        />
      )}

      <ConfirmDeleteModal
        open={deleteTarget != null}
        title="Delete this item?"
        message="This removes the item from the examiner's report. An item that has been quoted, booked or worked on cannot be deleted."
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      {showBackfill && (
        <BackfillModal
          onClose={() => setShowBackfill(false)}
          onDone={() => {
            setNotice("Existing reports brought in.");
            void load();
          }}
        />
      )}
    </div>
  );
}
